import { logger } from '@/utils/logger'
import { resolveApiBaseUrl } from '@/config/apiBaseUrl'
import { resolveApplicationApiUrl } from '@/services/apiRouteResolver'
import { client as generatedApiClient } from '@/generated/openapi/client.gen'
import {
  getAirportsByIdentWeatherMetar,
  getAirportsByIdentWeatherTaf,
  getFlightsMetrics,
  getSchedulesUpcoming,
  getUsers,
  getUsersMe,
  postAdminQuery,
} from '@/generated/openapi'
import { ApiError } from '@/services/apiErrors'
import {
  notificationAdminService,
  type NotificationAcknowledgementListParams,
  type NotificationAcknowledgementRecord,
  type NotificationAdminListParams,
  type NotificationBroadcastListParams,
  type NotificationBroadcastListResult,
  type NotificationBroadcastRequest,
  type NotificationBroadcastResult,
  type NotificationDeliveryListParams,
  type NotificationDeliveryRecord,
  type NotificationEscalationListParams,
  type NotificationEscalationRecord,
  type NotificationEventListParams,
  type NotificationEventRecord,
  type NotificationRecord,
  type NotificationTemplateListParams,
  type NotificationTemplateRecord,
  type NotificationTemplateUpsertRequest,
} from '@/services/notificationAdmin'
import type {
  WeatherCacheMetadata,
  WeatherReportDataWeatherMetarSummary,
  WeatherReportDataWeatherTafSummary,
  AdminSqlQueryOpenApiResponse,
} from '@/generated/openapi'

export { ApiError } from '@/services/apiErrors'
export type {
  NotificationAcknowledgementSource,
  NotificationAcknowledgementListParams,
  NotificationAcknowledgementRecord,
  NotificationAdminCategory,
  NotificationAdminListParams,
  NotificationAdminRecord,
  NotificationBroadcastListParams,
  NotificationBroadcastListResult,
  NotificationBroadcastListItem,
  NotificationBroadcastListStatus,
  NotificationBroadcastListSummary,
  NotificationBroadcastPriority,
  NotificationBroadcastRequest,
  NotificationBroadcastResult,
  NotificationBroadcastTargetGroup,
  NotificationDeliveryChannel,
  NotificationDeliveryListParams,
  NotificationDeliveryRecord,
  NotificationDeliveryV1Status,
  NotificationEscalationListParams,
  NotificationEscalationRecord,
  NotificationEscalationStatus,
  NotificationEventCategory,
  NotificationEventListParams,
  NotificationEventProcessingStatus,
  NotificationEventRecord,
  NotificationGenerationResult,
  NotificationRecord,
  NotificationRecordCategory,
  NotificationTemplateChannel,
  NotificationTemplateListParams,
  NotificationTemplateRecord,
  NotificationTemplateUpsertRequest,
} from '@/services/notificationAdmin'

// API Configuration and Base Service
const API_BASE_URL = resolveApiBaseUrl(import.meta.env.VITE_API_BASE_URL, import.meta.env.MODE)
const GENERATED_API_BASE_URL = resolveApplicationApiUrl(API_BASE_URL, '/v1')
const MAX_FLIGHT_LIST_DRAIN_PAGES = 100
generatedApiClient.setConfig({
  baseUrl: GENERATED_API_BASE_URL,
  auth: () => apiService.getAccessToken() ?? undefined,
})

const WEATHER_METAR_SUMMARY_KEYS = {
  windDirectionDegrees: 'wind_direction_degrees',
  windSpeedKnots: 'wind_speed_knots',
  visibilityMeters: 'visibility_meters',
  temperatureCelsius: 'temperature_celsius',
  dewpointCelsius: 'dewpoint_celsius',
  altimeterHpa: 'altimeter_hpa',
} as const

const WEATHER_TAF_SUMMARY_KEYS = {
  validFrom: 'valid_from',
  validTo: 'valid_to',
  worstFlightCategory: 'worst_flight_category',
} as const

export interface ApiResponse<T = unknown> {
  success?: boolean
  'success?'?: boolean // Rails sometimes returns this format
  failure?: boolean
  payload?: T
  data?: T
  user?: unknown
  error?: string
  error_code?: string
  status?: string
  validation_errors?: string[]
}

export interface AdminQueryResult {
  columns: string[]
  rows: unknown[][]
  page: number
  page_size: number
  has_next_page: boolean
}

export interface LoginRequest {
  username: string
  password: string
  rememberSession?: boolean
}

interface AuthTokenPair {
  access_token: string
  refresh_token: string
  access_token_expiry: string
  refresh_token_expiry?: string
}

interface RequestRetryState {
  refreshedAfter401?: boolean
}

function readSummaryNumber(summary: Record<string, unknown>, key: string): number | undefined {
  return typeof summary[key] === 'number' ? (summary[key] as number) : undefined
}

export const ACCESS_TOKEN_EXPIRY_STORAGE_KEY = 'falcon_v2_access_token_expiry'
export const TOKEN_TIMESTAMP_STORAGE_KEY = 'falcon_v2_token_timestamp'
const LEGACY_REFRESH_TOKEN_STORAGE_KEY = 'falcon_refresh_token'
const LEGACY_REFRESH_TOKEN_EXPIRY_STORAGE_KEY = 'falcon_refresh_token_expiry'
type SessionExpiredHandler = () => Promise<void> | void

export interface AuthUser {
  id?: string
  username: string
  user_type?: string
  user_types?: string[]
  email?: string
  full_name?: string
  cpf?: string
  tax_id?: string
  birthday?: string
  phone?: string
  active?: boolean
  reset_password?: boolean
  must_change_password?: boolean
  email_verified?: boolean
  email_verified_at?: string | null
  phone_verified?: boolean
  phone_verified_at?: string | null
  phone_verification_required?: boolean
  unread_notifications_count?: number
  roles?: string[]
  [key: string]: unknown
}

export interface LoginResponse {
  access_token: string
  refresh_token: string
  access_token_expiry: string
  refresh_token_expiry?: string
}

export interface ChangePasswordRequest {
  current_password: string
  new_password: string
}

export interface EmailVerificationRequest {
  token: string
}

export interface PhoneVerificationRequest {
  code: string
}

export interface RefreshTokenResponse {
  access_token: string
  refresh_token: string
  access_token_expiry: string
  refresh_token_expiry?: string
}

export interface UserSummary extends AuthUser {
  preferred_name?: string
  full_legal_name?: string
  tax_id?: string
  birth_date?: string
  weight_kg?: number
  roles?: string[]
  active?: boolean
  created_at?: string
  updated_at?: string
  pilot_detail?: PilotDetailSummary
}

export interface PilotMedicalCertificateSummary {
  class?: string
  validity_date?: string
  validity_text?: string
  days_remaining?: number
  status?: string
  source_presence?: 'present' | 'missing' | 'removed'
  last_seen_at?: string
  missing_since?: string | null
}

export interface PilotLicenseSummary {
  identity_key?: string
  type?: string
  raw_type?: string
  source_presence?: 'present' | 'missing' | 'removed'
  first_seen_at?: string
  last_seen_at?: string
  missing_since?: string | null
  issue_date?: string
  issue_date_text?: string
  number?: string
  status?: string
  is_active?: boolean
}

export interface PilotRatingSummary {
  identity_key?: string
  type?: string
  raw_type?: string
  type_recognized?: boolean
  source_presence?: 'present' | 'missing' | 'removed'
  first_seen_at?: string
  last_seen_at?: string
  missing_since?: string | null
  validity_date?: string
  validity_text?: string
  function?: string
  status?: string
  is_expired?: boolean
  is_valid?: boolean
}

export interface PilotQualificationSummary {
  source?: string
  synced_at?: string
  licensed_aircrafts?: string[]
  ratings?: PilotRatingSummary[]
  licenses?: PilotLicenseSummary[]
  warnings?: QualificationWarning[]
}

export interface PilotDetailSummary {
  anac_code?: string
  legal_name?: string
  birth_date_text?: string
  company?: string
  observations?: string
  qualification?: PilotQualificationSummary
  verified_at?: string
  last_anac_sync_succeeded_at?: string | null
  medical_certificate_summary?: PilotMedicalCertificateSummary
}

export interface UpdateUserProfileRequest {
  preferred_name?: string
  email?: string
  phone?: string
  full_legal_name?: string
  tax_id?: string
  birth_date?: string
  weight_kg?: number
}

export interface UpdateUserRolesRequest {
  roles: string[]
  anac_code?: string
}

export interface CreateUserRequest {
  username: string
  email: string
  phone: string
  password: string
  roles: string[]
  full_legal_name: string
  tax_id: string
  birth_date: string
  preferred_name: string
  weight_kg: number
  emancipated: boolean
  anac_code?: string
}

export interface FlightChange {
  field: string
  old_value: string
  new_value: string
  changed_by: string
  changed_at: string
  comment?: string
}

export interface QualificationOverride {
  reason: string
  approved_by: string
  approved_at: string
}

export interface QualificationMismatch {
  code: string
  message: string
  blocked: boolean
  overridden: boolean
  override?: QualificationOverride | null
}

export interface QualificationWarning {
  code: string
  message: string
  severity: string
  expires_at?: string | null
}

export interface FlightRecord {
  ID: string
  DisplayCode?: string
  PilotID: string
  AircraftID: string
  OriginAirportID: string
  DestinationAirportID: string
  ScheduledStart: string
  DistanceNM: number
  EstimatedDurationMinutes?: number | null
  FuelKG: number
  FuelLiters: number
  Status: string
  AircraftWeightKG: number
  PayloadWeightKG: number
  PassengerWeightKG: number
  PilotWeightKG: number
  Notes?: string
  allow_overweight?: boolean
  overweight_justification?: string
  Overweight?: boolean
  CancellationReason?: string
  ChangeLog?: FlightChange[]
  QualificationWarnings?: QualificationWarning[]
  QualificationMismatch?: QualificationMismatch | null
  PlanningAt?: string | null
  PreFlightAt?: string | null
  EngineStartAt?: string | null
  TaxiingAt?: string | null
  TakeoffAt?: string | null
  ClimbAt?: string | null
  CruiseAt?: string | null
  DescentAt?: string | null
  LandingAt?: string | null
  EngineShutdownAt?: string | null
  CreatedAt?: string
  UpdatedAt?: string
  origin_airport?: AirportRouteSummary
  destination_airport?: AirportRouteSummary
  aircraft?: AircraftFlightSummary
  pilot?: PilotFlightSummary
}

export interface AirportRouteSummary {
  id?: string
  ident: string
  icao?: string
  iata?: string
  name?: string
}

export interface AircraftFlightSummary {
  id?: string
  tail_number?: string
  model?: string
  manufacturing_year?: number
  serial_number?: string
  registration_status?: string
}

export interface PilotFlightSummary {
  id?: string
  display_name?: string
  license_code?: string
  license_display?: string
}

type RawFlightRecord = {
  id?: string
  display_code?: string
  pilot_id?: string
  aircraft_id?: string
  origin_airport_id?: string
  destination_airport_id?: string
  scheduled_start?: string
  distance_nm?: number
  estimated_duration_minutes?: number | null
  fuel_kg?: number
  fuel_liters?: number
  status?: string
  aircraft_weight_kg?: number
  payload_weight_kg?: number
  passenger_weight_kg?: number
  pilot_weight_kg?: number
  notes?: string
  allow_overweight?: boolean
  overweight_justification?: string
  overweight?: boolean
  cancellation_reason?: string
  change_log?: FlightChange[]
  qualification_warnings?: QualificationWarning[]
  qualification_mismatch?: QualificationMismatch | null
  planning_at?: string | null
  pre_flight_at?: string | null
  engine_start_at?: string | null
  taxiing_at?: string | null
  takeoff_at?: string | null
  climb_at?: string | null
  cruise_at?: string | null
  descent_at?: string | null
  landing_at?: string | null
  engine_shutdown_at?: string | null
  created_at?: string
  updated_at?: string
  origin_airport?: AirportRouteSummary
  destination_airport?: AirportRouteSummary
  aircraft?: AircraftFlightSummary
  pilot?: PilotFlightSummary
}

function normalizeFlightRecord(raw: RawFlightRecord): FlightRecord {
  return {
    ID: raw.id ?? '',
    DisplayCode: raw.display_code,
    PilotID: raw.pilot_id ?? '',
    AircraftID: raw.aircraft_id ?? '',
    OriginAirportID: raw.origin_airport_id ?? '',
    DestinationAirportID: raw.destination_airport_id ?? '',
    ScheduledStart: raw.scheduled_start ?? '',
    DistanceNM: raw.distance_nm ?? 0,
    EstimatedDurationMinutes: raw.estimated_duration_minutes,
    FuelKG: raw.fuel_kg ?? 0,
    FuelLiters: raw.fuel_liters ?? 0,
    Status: raw.status ?? '',
    AircraftWeightKG: raw.aircraft_weight_kg ?? 0,
    PayloadWeightKG: raw.payload_weight_kg ?? 0,
    PassengerWeightKG: raw.passenger_weight_kg ?? 0,
    PilotWeightKG: raw.pilot_weight_kg ?? 0,
    Notes: raw.notes,
    allow_overweight: raw.allow_overweight,
    overweight_justification: raw.overweight_justification,
    Overweight: raw.overweight,
    CancellationReason: raw.cancellation_reason,
    ChangeLog: raw.change_log,
    QualificationWarnings: raw.qualification_warnings,
    QualificationMismatch: raw.qualification_mismatch,
    PlanningAt: raw.planning_at,
    PreFlightAt: raw.pre_flight_at,
    EngineStartAt: raw.engine_start_at,
    TaxiingAt: raw.taxiing_at,
    TakeoffAt: raw.takeoff_at,
    ClimbAt: raw.climb_at,
    CruiseAt: raw.cruise_at,
    DescentAt: raw.descent_at,
    LandingAt: raw.landing_at,
    EngineShutdownAt: raw.engine_shutdown_at,
    CreatedAt: raw.created_at,
    UpdatedAt: raw.updated_at,
    origin_airport: raw.origin_airport,
    destination_airport: raw.destination_airport,
    aircraft: raw.aircraft,
    pilot: raw.pilot,
  }
}

function normalizeFlightRecords(raw: unknown): FlightRecord[] {
  if (!Array.isArray(raw)) return []
  return raw
    .filter((flight): flight is RawFlightRecord => flight !== null && typeof flight === 'object')
    .map((flight) => normalizeFlightRecord(flight))
}

interface FlightListPagePayload {
  flights?: unknown
  limit?: number
  offset?: number
  has_next_page?: boolean
  next_offset?: number | null
}

function normalizeFlightListPage(raw: unknown): {
  flights: FlightRecord[]
  hasNextPage: boolean
  nextOffset: number | null
} {
  if (Array.isArray(raw)) {
    return {
      flights: normalizeFlightRecords(raw),
      hasNextPage: false,
      nextOffset: null,
    }
  }
  if (raw && typeof raw === 'object') {
    const page = raw as FlightListPagePayload
    return {
      flights: normalizeFlightRecords(page.flights),
      hasNextPage: page.has_next_page === true,
      nextOffset: typeof page.next_offset === 'number' ? page.next_offset : null,
    }
  }
  return { flights: [], hasNextPage: false, nextOffset: null }
}

function normalizeDailySchedules(raw: unknown): DailySchedule[] {
  if (!Array.isArray(raw)) return []
  return raw
    .filter(
      (day): day is { date?: string; flights?: unknown } => day !== null && typeof day === 'object',
    )
    .map((day) => ({
      date: day.date ?? '',
      flights: normalizeFlightRecords(day.flights),
    }))
}

export interface FlightLocationPoint {
  id?: string
  flight_id?: string
  client_point_id?: string
  recorded_at?: string
  latitude: number
  longitude: number
  altitude_meters?: number | null
  ground_speed_knots?: number | null
  accuracy_meters?: number | null
  recorded_by_user_id?: string
  source?: string
  removed_at?: string | null
  removed_by_display_name?: string
  received_at?: string
  created_at?: string
  updated_at?: string
}

export interface FlightLocationPointRemovalResponse {
  point?: FlightLocationPoint | null
  already_removed: boolean
  removed_at: string
}

export interface FlightLocationLatestResponse {
  point?: FlightLocationPoint | null
  freshness_seconds: number
}

export interface FlightLocationCursor {
  after_received_at: string
  after_id: string
}

export interface FlightLocationHistoryResponse {
  points: FlightLocationPoint[]
  next_cursor?: FlightLocationCursor | null
  has_more: boolean
}

export interface FlightRuleModeSegment {
  flight_id?: string
  mode: 'vfr' | 'ifr'
  effective_at: string
  client_mode_change_id?: string
  recorded_by_user_id?: string
  created_at?: string
  updated_at?: string
}

export interface FlightTimeClassification {
  flight_id: string
  vfr_duration_seconds: number
  ifr_duration_seconds: number
  flight_rules_total_duration_seconds: number
  day_duration_seconds: number | null
  night_duration_seconds: number | null
  day_night_total_duration_seconds: number | null
  day_night_classification_status: 'available' | 'unavailable'
  day_night_classification_basis?: string
  diagnostics?: string[] | null
  segments: FlightRuleModeSegment[] | null
  canonical_completed_duration_unchanged: boolean
}

export interface FlightMetricsRange {
  from?: string | null
  to?: string | null
  timezone?: string
}

export interface FlightMetricsSummary {
  total_flights: number
  completed_flights: number
  cancelled_flights: number
  scheduled_flights: number
  in_progress_flights: number
  in_progress_flights_delta?: number
  upcoming_flights: number
  current_month_flights: number
  block_hours: number
  flight_hours: number
  total_distance_nm: number
  total_fuel_kg: number
  total_fuel_liters: number
  average_distance_nm: number
  average_block_hours: number
}

export interface FlightStatusMetric {
  status: string
  flights: number
}

export interface FlightMonthlyMetric {
  month: string
  flights: number
  completed_flights: number
  cancelled_flights: number
  block_hours: number
  flight_hours: number
  distance_nm: number
}

export interface FlightMetrics {
  range?: FlightMetricsRange
  summary: FlightMetricsSummary
  statuses: FlightStatusMetric[]
  monthly: FlightMonthlyMetric[]
}

export interface DocumentVaultDocument {
  id: string
  owner_type?: string
  owner_id?: string
  category?: string
  title?: string
  file_name?: string
  status?: string
  expires_at?: string | null
  renewal_due_at?: string | null
  reminder_lead_days?: number
  reminder_count?: number
  updated_at?: string
}

export interface DailySchedule {
  date: string
  flights: FlightRecord[]
}

export interface AirportRecord {
  id?: string
  ident: string
  icao?: string
  iata?: string
  name: string
  municipality?: string
  country?: string
  region?: string
  type?: string
  latitude?: number
  longitude?: number
  elevation_m?: number
  scheduled_service?: boolean
  gps_code?: string
  local_code?: string
  home_link?: string
  wikipedia_link?: string
  runway_length_m?: number
  service_fields?: string[]
  distance_km?: number
  distance_nm?: number
  updated_at?: string
}

export interface AirportFrequency {
  type: string
  frequency?: string
  description?: string
}

export interface ParsedWeatherTime {
  day?: number
  hour?: number
  minute?: number
}

export interface ParsedMETAR {
  observed?: ParsedWeatherTime
  report_type?: string
  station?: string
  expired?: boolean
  flight_category?: string
  raw_text?: string
  ceiling_feet?: number
  cloud_layers?: string[]
  visibility?: string
  wind?: string
  visibility_meters?: number
  wind_direction_degrees?: number
  wind_speed_knots?: number
  temperature_celsius?: number
  dewpoint_celsius?: number
  altimeter_hpa?: number
  observed_at?: string
}

export interface AirportWeatherCache {
  provider?: string
  provider_version?: string
  provider_status?: number
  state?: 'PENDING' | 'READY' | 'NO_REPORT' | 'FAILED'
  cached_at?: string | null
  expires_at?: string | null
  stale?: boolean
}

type AirportWeatherCacheState = NonNullable<AirportWeatherCache['state']>

export interface AirportWeatherReport<TSummary = Record<string, unknown>> {
  report_type: 'METAR' | 'TAF'
  airport_icao: string
  payload?: unknown[]
  summary?: TSummary | null
  cache: AirportWeatherCache
}

export interface AirportWeatherPayload {
  metar?: AirportWeatherReport<ParsedMETAR>
  taf?: AirportWeatherReport<Record<string, unknown>>
}

export interface AirportFrequenciesPayload {
  ident: string
  frequencies: AirportFrequency[]
}

export interface AirportDistancePoint {
  ident?: string
  name?: string
  latitude?: number
  longitude?: number
}

export interface AirportDistanceResult {
  distance_nm: number
  distance_km: number
  cached?: boolean
  from_airport?: AirportDistancePoint
  to_airport?: AirportDistancePoint
}

export interface AirportImportResult {
  started_at?: string
  completed_at?: string
  source_url?: string
  airports_seen?: number
  imported?: number
  updated?: number
  skipped?: number
  failed?: number
  errors?: number
  total_processed?: number
  changed?: boolean
  files?: AirportImportFileResult[]
}

export interface AirportImportFileResult {
  file: string
  status: string
  processed?: number
  imported?: number
  updated?: number
  skipped?: number
  warnings?: number
  errors?: number
  message?: string
}

export interface AirportRunway {
  name: string
  length_m?: number
  width_m?: number
  surface?: string
  lighted?: boolean
  closed?: boolean
}

export interface NavaidRecord {
  id?: string
  identifier: string
  name: string
  type: string
  frequency_khz?: number
  latitude: number
  longitude: number
  elevation_m?: number
  iso_country?: string
  usage_type?: string
  power?: string
  associated_airport_ident?: string
  waypoint_id?: string
}

export interface WaypointRecord {
  id?: string
  ourairports_id?: string
  identifier: string
  name: string
  type: string
  latitude: number
  longitude: number
  elevation_m?: number
  iso_country?: string
  associated_airport_ident?: string
}
export interface DatasetVersion {
  id?: string
  source_repo: string
  source_commit: string
  source_committed_at: string
  imported_at: string
  airports_count: number
  runways_count: number
  frequencies_count: number
  navaids_count: number
  total_count: number
}

export interface AirportImportExecution {
  id?: string
  source_repo: string
  source_commit: string
  source_committed_at: string
  executed_at: string
  succeeded: boolean
  imported: number
  updated: number
  errors: number
  warnings?: number
  total_processed: number
  files?: AirportImportFileResult[]
  import_error?: string
  import_hash?: string
  created_at?: string
  updated_at?: string
}

export interface AirportImportStatus {
  last_import_at?: string
  last_import_result?: AirportImportResult
  last_import_error?: string
  last_import_hash?: string
  in_progress?: boolean
  current_file?: string
  files?: AirportImportFileResult[]
}

export interface AirportImportHistoryPayload {
  latest_import?: AirportImportStatus
  history: DatasetVersion[]
  import_history?: AirportImportExecution[]
}

export interface ANACSyncStatus {
  last_run_at?: string
  last_success_at?: string
  last_error?: string
  last_processed?: number
  last_succeeded?: number
  last_failed?: number
}

export interface JobStatusPayload {
  airport_import?: AirportImportStatus
  anac_sync?: ANACSyncStatus
}

export interface ReportingRange {
  from?: string | null
  to?: string | null
}

export interface SafetyTrendPoint {
  date: string
  total_flights: number
  completed_flights: number
  cancelled_flights: number
  overweight_flights: number
  missing_phase_log_flights: number
  derived_safety_events: number
}

export interface SafetyReport {
  total_flights: number
  completed_flights: number
  cancelled_flights: number
  overweight_flights: number
  flights_missing_phase_log: number
  completion_rate: number
  overweight_rate: number
  derived_safety_event_rate: number
  trend: SafetyTrendPoint[]
}

export interface PilotPerformanceSummary {
  pilot_id: string
  pilot_name: string
  flights: number
  completed_flights: number
  cancelled_flights: number
  block_hours: number
  distance_nm: number
  fuel_kg: number
  average_fuel_kg_per_nm: number
  on_time_departure_rate: number
  performance_score: number
  last_flight_at?: string
  currency_status?: string
  has_verified_anac?: boolean
  has_active_license?: boolean
  has_expired_credentials?: boolean
}

export interface FleetUsageSummary {
  aircraft_id: string
  tail_number?: string
  aircraft_type?: string
  flights: number
  completed_flights: number
  cancelled_flights: number
  block_hours: number
  distance_nm: number
  fuel_kg: number
  average_fuel_kg_per_nm: number
  utilized_days: number
  utilization_rate: number
  overweight_flights: number
  missing_aircraft_setup?: boolean
}

export interface PilotComplianceSummary {
  pilot_id: string
  pilot_name: string
  has_verified_anac: boolean
  has_active_license: boolean
  has_expired_credentials: boolean
  flights: number
}

export interface ComplianceReport {
  total_flights: number
  compliant_flights: number
  flights_without_aircraft: number
  flights_missing_phase_log: number
  overweight_flights: number
  flights_with_unverified_pilot: number
  flights_with_expired_credentials: number
  compliance_rate: number
  pilot_summaries: PilotComplianceSummary[]
}

export interface FlightOperationsReport {
  generated_at: string
  range?: ReportingRange
  safety: SafetyReport
  pilot_performance: PilotPerformanceSummary[]
  fleet_usage: FleetUsageSummary[]
  fleet_status?: FleetStatusSummary[]
  compliance: ComplianceReport
  active_aircraft_count?: number
  recent_activity?: RecentActivityItem[]
}

export interface RecentActivityItem {
  id: string
  flight_id: string
  pilot_id: string
  pilot_name: string
  aircraft_id?: string
  aircraft_tail_number?: string
  kind: string
  summary: string
  details?: string
  status?: string
  occurred_at: string
}

export interface FleetStatusSummary {
  status: string
  count: number
  status_key: string
}

export interface QualificationReportItem {
  flight_id: string
  pilot_id: string
  aircraft_id: string
  aircraft_type_code?: string
  scheduled_start: string
  status: string
  blocked: boolean
  warnings?: QualificationWarning[]
  mismatch?: QualificationMismatch | null
}

export interface QualificationReport {
  generated_at: string
  items: QualificationReportItem[]
}

export interface AircraftOwner {
  user_id?: string
  owner_id?: string
  name?: string
  username?: string
  email?: string
  quota_percent?: number
}

export interface AircraftTypeRecord {
  id?: string
  icao_type_code?: string
  manufacturer?: string
  model?: string
  description?: string
  engine_type?: string
  fuel_type?: string
  fuel_density_kg_per_l?: number
  created_at?: string
  updated_at?: string
}

export interface AircraftWeightSpecificationRecord {
  aircraft_id?: string
  empty_weight_kg?: number
  empty_weight_arm_m?: number
  max_takeoff_weight_kg?: number
  max_landing_weight_kg?: number
  max_zero_fuel_weight_kg?: number
  useful_load_kg?: number
  baggage_capacity_kg?: number
  fuel_arm_m?: number
  pilot_arm_m?: number
  passenger_arm_m?: number
  baggage_arm_m?: number
  forward_cg_limit_m?: number
  aft_cg_limit_m?: number
  created_at?: string
  updated_at?: string
}

export interface AircraftANACSyncStateRecord {
  aircraft_id?: string
  last_attempt_at?: string | null
  last_success_at?: string | null
  last_error?: string
  consecutive_failures?: number
  owner_name?: string
  owner_document?: string
  owner_share_percent?: number
  operator_name?: string
  operator_document?: string
  year_of_manufacture?: number
  serial_number?: string
  certification_category?: string
  pilot_license_type?: string
  aircraft_class?: string
  authorized_flight_type?: string
  minimum_crew?: number
  seat_count?: number
  passenger_capacity?: number
  registration_category_note?: string
  operation_status_note?: string
  lien?: string
  purchase_date?: string | null
  purchase_date_text?: string
  certificate_of_airworthiness_expiry?: string | null
  certificate_of_airworthiness_expiry_text?: string
  airworthiness_status?: string
  airworthiness_reason?: string
  last_changed_fields?: string[] | null
  last_synced_record_hash?: string
  last_synced_record?: Record<string, unknown> | null
  created_at?: string
  updated_at?: string
}

export interface AircraftRecord {
  id: string
  tail_number: string
  aircraft_type?: AircraftTypeRecord
  capacity_kg?: number
  operational_range_km?: number
  weight_specification?: AircraftWeightSpecificationRecord | null
  anac_sync_state?: AircraftANACSyncStateRecord | null
  anac_syncable?: boolean
  active?: boolean
  created_at?: string
  updated_at?: string
}

class ApiService {
  private baseURL: string
  private refreshAccessTokenPromise: Promise<boolean> | null
  private sessionExpiredFlowPromise: Promise<void> | null
  private sessionExpiredHandler: SessionExpiredHandler | null = null
  private accessToken: string | null = null

  constructor() {
    this.baseURL = API_BASE_URL
    this.refreshAccessTokenPromise = null
    this.sessionExpiredFlowPromise = null
  }

  private buildApplicationUrl(endpoint: string): string {
    const resolvedURL = resolveApplicationApiUrl(this.baseURL, endpoint)
    logger.debug(`Original URL: ${endpoint} - Resolved URL: ${resolvedURL}`)
    return resolvedURL
  }

  private async parseApiResponse<T>(response: Response): Promise<ApiResponse<T>> {
    const contentType = response.headers.get('content-type')

    if (contentType && contentType.includes('application/json')) {
      const body = (await response.json()) as Record<string, unknown>
      const normalized: ApiResponse<T> = { ...body } as ApiResponse<T>

      // Canonical contract first: status/data/error.
      // Legacy envelopes remain supported as a compatibility bridge until every
      // backend path speaks the canonical shape.
      if (normalized.payload === undefined && body.data !== undefined) {
        normalized.payload = body.data as T
      }
      if (normalized.data === undefined && body.payload !== undefined) {
        normalized.data = body.payload as T
      }

      if (body.error && typeof body.error === 'object' && !Array.isArray(body.error)) {
        const errorBody = body.error as Record<string, unknown>
        const message =
          typeof errorBody.message === 'string' && errorBody.message.trim()
            ? errorBody.message
            : typeof errorBody.code === 'string' && errorBody.code.trim()
              ? errorBody.code
              : JSON.stringify(errorBody)
        normalized.error = message
        if (typeof errorBody.code === 'string' && errorBody.code.trim()) {
          normalized.error_code = errorBody.code
        }
      } else if (typeof body.error === 'string') {
        normalized.error = body.error
      }

      if (typeof body.status === 'string' && body.status.trim()) {
        normalized.status = body.status
        if (normalized.success === undefined && normalized['success?'] === undefined) {
          normalized.success = body.status === 'success'
          normalized.failure = body.status !== 'success'
        }
      }

      return normalized
    }

    const text = await response.text()
    return {
      success: response.ok,
      error: response.ok ? undefined : text || `HTTP ${response.status}`,
    }
  }

  private toDomainError(
    error?: string,
    fallback = 'Falha ao processar a resposta do servidor.',
  ): Error {
    return new Error(error || fallback)
  }

  private readGeneratedEnvelope<T>(
    response:
      | {
          data?: T
          error?: unknown
        }
      | undefined,
    fallbackMessage: string,
  ): T {
    if (response?.error) {
      throw this.toDomainError(
        typeof response.error === 'string' ? response.error : undefined,
        fallbackMessage,
      )
    }

    if (!response?.data) {
      throw new Error(fallbackMessage)
    }

    return response.data
  }

  private isSuccessfulApiResponse(response: ApiResponse<unknown>): boolean {
    if (typeof response.status === 'string') {
      return response.status === 'success'
    }
    // Legacy envelopes may still arrive from older backend paths.
    return Boolean(response.success) || Boolean(response['success?']) || response.failure !== true
  }

  private requireObject(value: unknown, context: string): Record<string, unknown> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      throw new Error(`${context} inválido: objeto esperado`)
    }
    return value as Record<string, unknown>
  }

  private requireString(value: unknown, field: string, context: string): string {
    if (typeof value !== 'string' || !value.trim()) {
      throw new Error(`${context} inválido: campo ${field} ausente`)
    }
    return value
  }

  private requireBoolean(value: unknown, field: string, context: string): boolean {
    if (typeof value !== 'boolean') {
      throw new Error(`${context} inválido: campo ${field} ausente`)
    }
    return value
  }

  private logRequestBody(body: BodyInit | null | undefined): unknown {
    if (body === undefined || body === null) {
      return undefined
    }

    if (typeof body !== 'string') {
      return body
    }

    try {
      return this.redactLogValue(JSON.parse(body))
    } catch (error) {
      logger.warn('Falha ao serializar body da requisição para log', error)
      return body
    }
  }

  private redactLogValue(value: unknown): unknown {
    if (Array.isArray(value)) {
      return value.map((entry) => this.redactLogValue(entry))
    }
    if (!value || typeof value !== 'object') {
      return value
    }

    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, entry]) => [
        key,
        this.isSensitiveLogField(key) ? '[REDACTED]' : this.redactLogValue(entry),
      ]),
    )
  }

  private isSensitiveLogField(key: string): boolean {
    const normalized = key.toLowerCase()
    return (
      normalized.includes('password') ||
      normalized.includes('token') ||
      normalized.includes('authorization') ||
      normalized.includes('phone') ||
      normalized === 'tax_id' ||
      normalized === 'cpf'
    )
  }

  private parseAuthUser(value: unknown, context: string): AuthUser {
    const record = this.requireObject(value, context)
    const user: AuthUser = {
      id: record.id === undefined ? undefined : this.requireString(record.id, 'id', context),
      username: this.requireString(record.username, 'username', context),
    }

    const optionalStringFields = [
      'email',
      'full_name',
      'cpf',
      'tax_id',
      'birthday',
      'phone',
    ] as const
    for (const field of optionalStringFields) {
      const candidate = record[field]
      if (typeof candidate === 'string') {
        user[field] = candidate
      }
    }

    const optionalBooleanFields = [
      'active',
      'reset_password',
      'must_change_password',
      'email_verified',
      'phone_verified',
      'phone_verification_required',
    ] as const
    for (const field of optionalBooleanFields) {
      const candidate = record[field]
      if (typeof candidate === 'boolean') {
        user[field] = candidate
      }
    }

    if (typeof record.email_verified_at === 'string' || record.email_verified_at === null) {
      user.email_verified_at = record.email_verified_at
    }
    if (typeof record.phone_verified_at === 'string' || record.phone_verified_at === null) {
      user.phone_verified_at = record.phone_verified_at
    }

    if (!Array.isArray(record.roles)) {
      throw new Error(`${context} inválido: campo roles ausente`)
    }
    user.roles = record.roles.filter((entry): entry is string => typeof entry === 'string')

    return user
  }

  private parseUserSummary(value: unknown, context: string): UserSummary {
    const record = this.requireObject(value, context)
    const user = this.parseAuthUser(record, context)

    const optionalStringFields = [
      'preferred_name',
      'full_legal_name',
      'tax_id',
      'birth_date',
      'created_at',
      'updated_at',
    ] as const
    for (const field of optionalStringFields) {
      const candidate = record[field]
      if (typeof candidate === 'string') {
        user[field] = candidate
      }
    }

    if (typeof record.weight_kg === 'number') {
      user.weight_kg = record.weight_kg
    }

    if (
      record.pilot_detail &&
      typeof record.pilot_detail === 'object' &&
      !Array.isArray(record.pilot_detail)
    ) {
      user.pilot_detail = record.pilot_detail as PilotDetailSummary
    }

    return user
  }

  private parseUserList(value: unknown, context: string): UserSummary[] {
    if (!Array.isArray(value)) {
      throw new Error(`${context} inválido: lista esperada`)
    }

    return value.map((entry) => this.parseUserSummary(entry, context))
  }

  private parseAuthTokenPair(data: ApiResponse<AuthTokenPair>, context: string): AuthTokenPair {
    const payload = this.requireObject(data.payload, context)
    const accessToken = this.requireString(payload.access_token, 'access_token', context)
    const refreshToken = this.requireString(payload.refresh_token, 'refresh_token', context)
    const refreshTokenExpiry =
      typeof payload.refresh_token_expiry === 'string' && payload.refresh_token_expiry.trim()
        ? payload.refresh_token_expiry
        : undefined

    return {
      access_token: accessToken,
      refresh_token: refreshToken,
      access_token_expiry: this.readJwtExpiry(accessToken, context),
      refresh_token_expiry: refreshTokenExpiry,
    }
  }

  private parseLoginPayload(data: ApiResponse<LoginResponse>): LoginResponse {
    this.requireObject(data.payload, 'Resposta de login')
    return this.parseAuthTokenPair(data as ApiResponse<AuthTokenPair>, 'Resposta de login')
  }

  private readJwtExpiry(token: string, context: string): string {
    const segments = token.split('.')
    if (segments.length !== 3) {
      throw new Error(`${context} inválido: token JWT malformado`)
    }

    try {
      const normalized = segments[1].replace(/-/g, '+').replace(/_/g, '/')
      const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=')
      const payload = JSON.parse(atob(padded)) as { exp?: unknown }
      if (typeof payload.exp !== 'number' || Number.isNaN(payload.exp)) {
        throw new Error()
      }

      return new Date(payload.exp * 1000).toISOString()
    } catch {
      throw new Error(`${context} inválido: claim exp ausente ou inválida`)
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    retryState: RequestRetryState = {},
  ): Promise<ApiResponse<T>> {
    const url = this.buildApplicationUrl(endpoint)
    const method = options.method || 'GET'

    const defaultHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    }

    // Add authorization header if token exists
    const token = this.getAccessToken()
    if (token) {
      defaultHeaders['Authorization'] = `Bearer ${token}`
    }

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    }

    // Log the outgoing request
    logger.apiRequest(method, url, this.logRequestBody(options.body as BodyInit | null | undefined))

    try {
      const response = await fetch(url, config)

      // Handle different response types
      const data = await this.parseApiResponse<T>(response)

      // Handle 401 Unauthorized - try to refresh token
      if (response.status === 401 && !retryState.refreshedAfter401) {
        const refreshed = await this.refreshAccessToken()
        if (refreshed) {
          // Retry the original request with new token
          return this.request(endpoint, options, { refreshedAfter401: true })
        } else {
          // Refresh failed, redirect to login once per burst of concurrent 401s.
          await this.handleSessionExpired()
          throw new Error('Session expired')
        }
      }

      // Log the response
      logger.apiResponse(method, url, response.status, data)

      if (!response.ok) {
        logger.apiError(method, url, data.error || `HTTP ${response.status}`)
        throw new ApiError(data.error || `HTTP ${response.status}`, data.error_code)
      }

      return data
    } catch (error) {
      logger.apiError(method, url, error)

      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Não foi possível conectar ao servidor. Verifique sua conexão.')
      }

      throw error
    }
  }

  setSessionExpiredHandler(handler: SessionExpiredHandler | null): void {
    this.sessionExpiredHandler = handler
  }

  private async notifySessionExpired(): Promise<void> {
    if (this.sessionExpiredHandler) {
      await this.sessionExpiredHandler()
      return
    }

    window.location.href = '/'
  }

  private async handleSessionExpired(): Promise<void> {
    if (this.sessionExpiredFlowPromise) {
      await this.sessionExpiredFlowPromise
      return
    }

    const flow = (async () => {
      await this.logout()
      await this.notifySessionExpired()
    })()

    this.sessionExpiredFlowPromise = flow
      .catch((error) => {
        logger.apiError('POST', this.buildApplicationUrl('/auth/logout'), error)
      })
      .finally(() => {
        this.sessionExpiredFlowPromise = null
      })

    await this.sessionExpiredFlowPromise
  }

  private buildQuery(
    params?: Record<string, string | number | boolean | null | undefined>,
  ): string {
    if (!params) return ''

    const search = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null || value === '') continue
      search.set(key, String(value))
    }

    const query = search.toString()
    return query ? `?${query}` : ''
  }

  // Authentication methods
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    // Make direct fetch call to avoid CORS preflight OPTIONS request
    const url = this.buildApplicationUrl('/auth/login')

    logger.authLogin(credentials.username)
    logger.apiRequest('POST', url, { username: credentials.username, password: '[HIDDEN]' })

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          username: credentials.username,
          password: credentials.password,
          remember_session: credentials.rememberSession ?? false,
        }),
      })

      const data = await this.parseApiResponse<LoginResponse>(response)

      logger.apiResponse('POST', url, response.status, {
        success: data.success,
        error: data.error,
        rawResponse: data,
      })

      // Check for success in both formats (success or success?)
      const isSuccess = this.isSuccessfulApiResponse(data)

      if (response.ok && isSuccess && data.payload) {
        const payload = this.parseLoginPayload(data)
        this.setTokens(payload.access_token, payload.access_token_expiry)
        logger.authLoginSuccess(credentials.username)
        return payload
      }

      const contentType = response.headers.get('content-type') || ''
      const isJSONResponse = contentType.includes('application/json')
      const loginErrorMessage = isJSONResponse
        ? data.error || 'Falha ao realizar login.'
        : 'Não foi possível autenticar no momento. Tente novamente em instantes.'
      const error = this.toDomainError(loginErrorMessage, 'Falha ao realizar login.')
      logger.authLoginError(credentials.username, error)
      throw error
    } catch (error) {
      logger.authLoginError(credentials.username, error)

      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Não foi possível conectar ao servidor. Verifique sua conexão.')
      }

      throw error
    }
  }

  async refreshAccessToken(): Promise<boolean> {
    logger.authTokenRefresh()

    if (this.refreshAccessTokenPromise) {
      return this.refreshAccessTokenPromise
    }

    const refreshPromise = (async () => {
      const response = await fetch(this.buildApplicationUrl('/auth/refresh'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'include',
      })

      const data = await this.parseApiResponse<RefreshTokenResponse>(response)
      const isSuccess = response.ok && this.isSuccessfulApiResponse(data)
      if (isSuccess && data.payload) {
        const payload = this.parseAuthTokenPair(
          {
            payload: data.payload,
          },
          'Resposta de refresh',
        )
        this.setTokens(payload.access_token, payload.access_token_expiry)
        logger.authTokenRefreshSuccess()
        return true
      }

      logger.authTokenRefreshError(data.error || `HTTP ${response.status}`)
      return false
    })()

    this.refreshAccessTokenPromise = refreshPromise
      .catch((error) => {
        logger.authTokenRefreshError(error)
        return false
      })
      .finally(() => {
        this.refreshAccessTokenPromise = null
      })

    return this.refreshAccessTokenPromise
  }

  async logout(options: { preserveLocalSessionOnFailure?: boolean } = {}): Promise<boolean> {
    logger.authLogout()
    let success = false

    const url = this.buildApplicationUrl('/auth/logout')
    const requestPayload = undefined

    try {
      logger.apiRequest('POST', url, requestPayload)

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'include',
      })

      const data = await this.parseApiResponse(response)

      logger.apiResponse('POST', url, response.status, data)

      const isSuccess = response.ok && this.isSuccessfulApiResponse(data)
      if (!isSuccess) {
        throw new Error(data.error || `HTTP ${response.status}`)
      }

      success = true
    } catch (error) {
      logger.apiError('POST', url, error)
    } finally {
      if (success || !options.preserveLocalSessionOnFailure) {
        this.clearTokens()
      }
    }

    return success
  }

  async changePassword(payload: ChangePasswordRequest): Promise<void> {
    // Direct fetch — bypass the shared request() handler so a 401 (wrong current
    // password) surfaces as a domain error instead of triggering token refresh/logout.
    const url = this.buildApplicationUrl('/auth/change-password')
    const token = this.getAccessToken()

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    })

    const data = await this.parseApiResponse(response)

    const isSuccess = response.ok && this.isSuccessfulApiResponse(data)
    if (!isSuccess) {
      throw this.toDomainError(data.error, `HTTP ${response.status}`)
    }
  }

  async resendEmailVerification(): Promise<void> {
    await this.post('/auth/email-verification/resend')
  }

  async verifyEmailVerification(payload: EmailVerificationRequest): Promise<void> {
    await this.post('/auth/email-verification/verify', payload)
  }

  async requestPhoneVerification(): Promise<void> {
    await this.post('/auth/phone-verification/request')
  }

  async verifyPhoneVerification(payload: PhoneVerificationRequest): Promise<void> {
    await this.post('/auth/phone-verification/verify', payload)
  }

  // Token management
  private setTokens(accessToken: string, accessTokenExpiry?: string): void {
    this.accessToken = accessToken
    sessionStorage.setItem(TOKEN_TIMESTAMP_STORAGE_KEY, Date.now().toString())
    if (accessTokenExpiry) {
      sessionStorage.setItem(ACCESS_TOKEN_EXPIRY_STORAGE_KEY, accessTokenExpiry)
    }
    localStorage.removeItem(LEGACY_REFRESH_TOKEN_STORAGE_KEY)
    localStorage.removeItem(LEGACY_REFRESH_TOKEN_EXPIRY_STORAGE_KEY)
  }

  getAccessToken(): string | null {
    if (!this.accessToken) {
      return null
    }

    const storedExpiry = sessionStorage.getItem(ACCESS_TOKEN_EXPIRY_STORAGE_KEY)
    const storedTimestamp = sessionStorage.getItem(TOKEN_TIMESTAMP_STORAGE_KEY)
    if (!storedExpiry && !storedTimestamp) {
      return this.accessToken
    }

    if (!storedExpiry || !storedTimestamp || Number.isNaN(Number(storedTimestamp))) {
      logger.warn('Token de acesso rejeitado por metadados ausentes ou inválidos')
      this.clearTokens()
      return null
    }

    try {
      const jwtExpiry = this.readJwtExpiry(this.accessToken, 'Token de acesso armazenado')
      if (jwtExpiry !== storedExpiry) {
        logger.warn('Token de acesso rejeitado por inconsistência entre JWT e metadados')
        this.clearTokens()
        return null
      }
    } catch (error) {
      logger.warn('Token de acesso rejeitado por JWT inválido em memória', error)
      this.clearTokens()
      return null
    }

    return this.accessToken
  }

  hasAccessToken(): boolean {
    return this.getAccessToken() !== null
  }

  private clearTokens(): void {
    this.accessToken = null
    sessionStorage.removeItem(ACCESS_TOKEN_EXPIRY_STORAGE_KEY)
    sessionStorage.removeItem(TOKEN_TIMESTAMP_STORAGE_KEY)
    localStorage.removeItem(LEGACY_REFRESH_TOKEN_STORAGE_KEY)
    localStorage.removeItem(LEGACY_REFRESH_TOKEN_EXPIRY_STORAGE_KEY)
  }

  // Utility methods
  isAuthenticated(): boolean {
    return !!this.getAccessToken()
  }

  // User profile methods
  async getCurrentUser(): Promise<AuthUser> {
    const response = await getUsersMe({ client: generatedApiClient })
    const responseBody = this.readGeneratedEnvelope(response, 'Failed to get user profile')
    return this.parseAuthUser(responseBody.data, 'Resposta de perfil')
  }

  async getCurrentUserProfile(): Promise<UserSummary> {
    const response = await getUsersMe({ client: generatedApiClient })
    const responseBody = this.readGeneratedEnvelope(
      response,
      'Falha ao carregar o perfil do usuário',
    )
    return this.parseUserSummary(responseBody.data, 'Resposta de perfil')
  }

  async executeAdminQuery(sql: string, page = 1): Promise<AdminQueryResult> {
    const response = await postAdminQuery({
      client: generatedApiClient,
      body: { sql, page },
    })
    if (response.error) {
      const envelope = this.requireObject(response.error, 'Erro da consulta administrativa')
      const errorBody = envelope.error
      if (errorBody && typeof errorBody === 'object' && !Array.isArray(errorBody)) {
        const fields = errorBody as Record<string, unknown>
        const message =
          typeof fields.message === 'string'
            ? fields.message
            : 'Falha ao executar consulta administrativa'
        const code = typeof fields.code === 'string' ? fields.code : undefined
        throw new ApiError(message, code)
      }
      throw new ApiError('Falha ao executar consulta administrativa')
    }

    const payload = response.data?.data as AdminSqlQueryOpenApiResponse | undefined
    if (!payload) {
      throw new ApiError('Falha ao executar consulta administrativa')
    }

    return {
      columns: payload.columns ?? [],
      rows: (payload.rows ?? []).filter((row): row is unknown[] => Array.isArray(row)),
      page: payload.page,
      page_size: payload.page_size,
      has_next_page: payload.has_next_page,
    }
  }

  // Generic API methods for future use
  async get<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'GET' })
  }

  async post<T>(
    endpoint: string,
    data?: unknown,
    options: RequestInit = {},
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  async put<T>(
    endpoint: string,
    data?: unknown,
    options: RequestInit = {},
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  async delete<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' })
  }

  async listUsers(options: RequestInit = {}): Promise<UserSummary[]> {
    const response = await getUsers({ client: generatedApiClient, ...options } as never)
    const responseBody = this.readGeneratedEnvelope(response, 'Falha ao carregar usuários')
    const payload = responseBody.data
    return this.parseUserList(payload, 'Lista de usuários')
  }

  async createUser(payload: CreateUserRequest): Promise<UserSummary> {
    const response = await this.post<UserSummary>('/users', payload)
    const isSuccess = this.isSuccessfulApiResponse(response)
    if (isSuccess && response.payload) {
      return response.payload as UserSummary
    }
    throw new Error(response.error || 'Falha ao criar usuário')
  }

  async updateUserProfile(userId: string, payload: UpdateUserProfileRequest): Promise<UserSummary> {
    const response = await this.request<UserSummary>(`/users/${encodeURIComponent(userId)}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    })
    const isSuccess = this.isSuccessfulApiResponse(response)
    if (isSuccess && response.payload) return response.payload as UserSummary
    throw new Error(response.error || 'Falha ao atualizar perfil do usuário')
  }

  async updateUserRoles(userId: string, payload: UpdateUserRolesRequest): Promise<UserSummary> {
    const response = await this.request<UserSummary>(`/users/${encodeURIComponent(userId)}/roles`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    })
    const isSuccess = this.isSuccessfulApiResponse(response)
    if (isSuccess && response.payload) return response.payload as UserSummary
    throw new Error(response.error || 'Falha ao atualizar papéis do usuário')
  }

  async overrideAccountSecurity(
    userId: string,
    payload: {
      email_verified?: boolean
      phone_verified?: boolean
      must_change_password?: boolean
      reason: string
    },
  ): Promise<UserSummary> {
    const response = await this.request<{ user?: UserSummary; [key: string]: unknown }>(
      `/users/${encodeURIComponent(userId)}/security-overrides`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
    )
    const isSuccess = this.isSuccessfulApiResponse(response)
    if (isSuccess && response.payload) {
      const payloadUser = response.payload.user
      if (payloadUser) return payloadUser
      return response.payload as UserSummary
    }
    throw new Error(response.error || 'Falha ao aplicar override de segurança')
  }

  async listFlights(params?: {
    pilot_id?: string
    aircraft_id?: string
    status?: string
    from?: string
    to?: string
    limit?: number
    offset?: number
  }): Promise<FlightRecord[]> {
    const requestedLimit = params?.limit
    const requestedOffset = params?.offset
    const shouldDrain = requestedLimit === undefined && requestedOffset === undefined
    const allFlights: FlightRecord[] = []
    let nextOffset = requestedOffset ?? 0
    let drainedPages = 0

    do {
      drainedPages += 1
      if (drainedPages > MAX_FLIGHT_LIST_DRAIN_PAGES) {
        throw new Error('Limite de paginação da lista de voos excedido')
      }
      const pageParams = {
        ...params,
        ...(shouldDrain ? { offset: nextOffset } : {}),
      }
      const response = await this.get<unknown>(`/flights${this.buildQuery(pageParams)}`)
      const isSuccess = this.isSuccessfulApiResponse(response)
      if (!isSuccess) throw new Error(response.error || 'Falha ao carregar voos')

      const page = normalizeFlightListPage(response.payload)
      if (!shouldDrain) return page.flights

      allFlights.push(...page.flights)
      if (!page.hasNextPage || page.nextOffset === null) break
      nextOffset = page.nextOffset
    } while (true)

    return allFlights
  }

  async listAircrafts(params?: { limit?: number }): Promise<AircraftRecord[]> {
    const response = await this.get<AircraftRecord[]>(`/aircrafts${this.buildQuery(params)}`)
    const isSuccess = this.isSuccessfulApiResponse(response)
    if (isSuccess) return (response.payload as AircraftRecord[] | undefined) ?? []
    throw new Error(response.error || 'Falha ao carregar aeronaves')
  }

  async getAircraftByRegistration(tailNumber: string): Promise<AircraftRecord> {
    const encodedTailNumber = encodeURIComponent(tailNumber)
    const response = await this.get<AircraftRecord>(
      `/aircrafts/by-registration/${encodedTailNumber}`,
    )
    const isSuccess = this.isSuccessfulApiResponse(response)
    if (isSuccess && response.payload) return response.payload as AircraftRecord
    throw new Error(response.error || 'Falha ao carregar aeronave')
  }

  async getFlight(id: string): Promise<FlightRecord> {
    const response = await this.get<FlightRecord>(`/flights/${id}`)
    const isSuccess = this.isSuccessfulApiResponse(response)
    if (isSuccess && response.payload)
      return normalizeFlightRecord(response.payload as RawFlightRecord)
    throw new Error(response.error || 'Falha ao carregar voo')
  }

  async getFlightTimeClassification(id: string): Promise<FlightTimeClassification> {
    const response = await this.get<FlightTimeClassification>(
      `/flights/${encodeURIComponent(id)}/time-classification`,
    )
    const isSuccess = this.isSuccessfulApiResponse(response)
    if (isSuccess && response.payload) return response.payload as FlightTimeClassification
    throw new Error(response.error || 'Falha ao carregar horas classificadas')
  }

  async getFlightLatestLocation(id: string): Promise<FlightLocationLatestResponse> {
    const response = await this.get<FlightLocationLatestResponse>(
      `/flights/${encodeURIComponent(id)}/locations/latest`,
    )
    const isSuccess = this.isSuccessfulApiResponse(response)
    if (isSuccess && response.payload) return response.payload as FlightLocationLatestResponse
    throw new Error(response.error || 'Falha ao carregar ponto sincronizado')
  }

  async getFlightLocationsPage(
    id: string,
    params?: {
      after_received_at?: string
      after_id?: string
      limit?: number
    },
  ): Promise<FlightLocationHistoryResponse> {
    const response = await this.get<FlightLocationHistoryResponse>(
      `/flights/${encodeURIComponent(id)}/locations${this.buildQuery(params)}`,
    )
    const isSuccess = this.isSuccessfulApiResponse(response)
    if (isSuccess && response.payload) return response.payload as FlightLocationHistoryResponse
    throw new Error(response.error || 'Falha ao carregar trajeto do voo')
  }

  async getFlightLocations(id: string, limit = 500): Promise<FlightLocationPoint[]> {
    const points: FlightLocationPoint[] = []
    let cursor: FlightLocationCursor | null | undefined = null
    for (let pageCount = 0; pageCount < 100; pageCount++) {
      const page = await this.getFlightLocationsPage(id, {
        after_received_at: cursor?.after_received_at,
        after_id: cursor?.after_id,
        limit,
      })
      points.push(...(page.points ?? []))
      cursor = page.next_cursor
      if (!page.has_more) return points
      if (!cursor) throw new Error('Resposta paginada sem cursor')
    }
    throw new Error('Limite de paginação do trajeto excedido')
  }

  async removeFlightLocationPoint(
    flightID: string,
    pointID: string,
    justification: string,
  ): Promise<FlightLocationPointRemovalResponse> {
    const response = await this.post<FlightLocationPointRemovalResponse>(
      `/admin/flights/${encodeURIComponent(flightID)}/locations/${encodeURIComponent(pointID)}/removal-requests`,
      { justification },
    )
    const isSuccess = this.isSuccessfulApiResponse(response)
    if (isSuccess && response.payload) return response.payload as FlightLocationPointRemovalResponse
    throw new ApiError(response.error || 'Falha ao corrigir ponto do trajeto', response.error_code)
  }

  async getFlightMetrics(params?: {
    from?: string
    to?: string
    tz?: string
  }): Promise<FlightMetrics> {
    if (params && Object.values(params).some((value) => value !== undefined && value !== null)) {
      const response = await this.get<FlightMetrics>(`/flights/metrics${this.buildQuery(params)}`)
      const isSuccess = this.isSuccessfulApiResponse(response)
      if (isSuccess && response.payload) return response.payload as FlightMetrics
      throw new Error(response.error || 'Falha ao carregar métricas de voo')
    }

    const response = await getFlightsMetrics({ client: generatedApiClient } as never)
    const responseBody = this.readGeneratedEnvelope(response, 'Falha ao carregar métricas de voo')
    return responseBody.data as FlightMetrics
  }

  async updateFlight(id: string, payload: Record<string, unknown>): Promise<FlightRecord> {
    const response = await this.request<FlightRecord>(`/flights/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    })
    const isSuccess = this.isSuccessfulApiResponse(response)
    if (isSuccess && response.payload)
      return normalizeFlightRecord(response.payload as RawFlightRecord)
    throw new Error(response.error || 'Falha ao atualizar voo')
  }

  async cancelFlight(id: string, reason: string): Promise<FlightRecord> {
    const response = await this.request<FlightRecord>(`/flights/${id}/cancel`, {
      method: 'PATCH',
      body: JSON.stringify({ reason }),
    })
    const isSuccess = this.isSuccessfulApiResponse(response)
    if (isSuccess && response.payload)
      return normalizeFlightRecord(response.payload as RawFlightRecord)
    throw new Error(response.error || 'Falha ao cancelar voo')
  }

  async logFlightPhase(id: string, phase: string, timestamp?: string): Promise<FlightRecord> {
    const response = await this.request<FlightRecord>(`/flights/${id}/phases/${phase}`, {
      method: 'POST',
      body: timestamp ? JSON.stringify({ timestamp }) : undefined,
    })
    const isSuccess = this.isSuccessfulApiResponse(response)
    if (isSuccess && response.payload)
      return normalizeFlightRecord(response.payload as RawFlightRecord)
    throw new Error(response.error || 'Falha ao registrar fase do voo')
  }

  async getScheduleWeekly(params?: { start?: string; tz?: string }): Promise<DailySchedule[]> {
    const response = await this.get<DailySchedule[]>(`/schedules/weekly${this.buildQuery(params)}`)
    const isSuccess = this.isSuccessfulApiResponse(response)
    if (isSuccess) return normalizeDailySchedules(response.payload)
    throw new Error(response.error || 'Falha ao carregar agenda semanal')
  }

  async getScheduleUpcoming(params?: { limit?: number; tz?: string }): Promise<FlightRecord[]> {
    if (params && Object.values(params).some((value) => value !== undefined && value !== null)) {
      const response = await this.get<FlightRecord[]>(
        `/schedules/upcoming${this.buildQuery(params)}`,
      )
      const isSuccess = this.isSuccessfulApiResponse(response)
      if (isSuccess) return normalizeFlightRecords(response.payload)
      throw new Error(response.error || 'Falha ao carregar próximos voos')
    }

    const response = await getSchedulesUpcoming({ client: generatedApiClient } as never)
    const responseBody = this.readGeneratedEnvelope(response, 'Falha ao carregar próximos voos')
    return normalizeFlightRecords(responseBody.data)
  }

  async getFlightOperationsReport(params?: {
    from?: string
    to?: string
    tz?: string
  }): Promise<FlightOperationsReport> {
    const response = await this.get<FlightOperationsReport>(
      `/reports/flight-operations${this.buildQuery(params)}`,
    )
    const isSuccess = this.isSuccessfulApiResponse(response)
    if (isSuccess && response.payload) return response.payload as FlightOperationsReport
    throw new Error(response.error || 'Falha ao carregar relatório operacional')
  }

  async searchAirports(params?: {
    q?: string
    country?: string
    region?: string
    type?: string
    major_only?: boolean
    scheduled_service?: boolean
    min_runway_m?: number
    limit?: number
    offset?: number
  }): Promise<AirportRecord[]> {
    const response = await this.get<AirportRecord[]>(`/airports${this.buildQuery(params)}`)
    const isSuccess = this.isSuccessfulApiResponse(response)
    if (isSuccess) return (response.payload as AirportRecord[] | undefined) ?? []
    throw new Error(response.error || 'Falha ao carregar aeroportos')
  }

  async getAirport(ident: string): Promise<AirportRecord> {
    const response = await this.get<AirportRecord>(`/airports/${encodeURIComponent(ident)}`)
    const isSuccess = this.isSuccessfulApiResponse(response)
    if (isSuccess && response.payload) return response.payload as AirportRecord
    throw new Error(response.error || 'Falha ao carregar aeroporto')
  }

  async getAirportWeather(ident: string, cache = true): Promise<AirportWeatherPayload> {
    void cache
    const [metarResponse, tafResponse] = await Promise.all([
      this.getAirportWeatherReport<WeatherReportDataWeatherMetarSummary>(() =>
        getAirportsByIdentWeatherMetar({ client: generatedApiClient, path: { ident } } as never),
      ),
      this.getAirportWeatherReport<WeatherReportDataWeatherTafSummary>(() =>
        getAirportsByIdentWeatherTaf({ client: generatedApiClient, path: { ident } } as never),
      ),
    ])

    return {
      metar: this.mapMetarWeatherReport(metarResponse.data),
      taf: this.mapTafWeatherReport(tafResponse.data),
    }
  }

  private async getAirportWeatherReport<TData>(
    request: () => Promise<{
      data?: {
        data: TData
        error?: unknown
        status: 'success' | 'error'
      }
      error?: unknown
      response?: Response
    }>,
  ): Promise<{
    data: TData
    error?: unknown
    status: 'success' | 'error'
  }> {
    const retryDelaysMs = [1000, 2000, 4000, 8000]
    let latestBody: {
      data: TData
      error?: unknown
      status: 'success' | 'error'
    } | null = null

    for (let attempt = 0; attempt <= retryDelaysMs.length; attempt++) {
      const response = await request()
      const body = this.readGeneratedEnvelope(
        response,
        'Falha ao carregar meteorologia do aeroporto',
      )
      latestBody = body

      if (response.response?.status !== 202 || attempt === retryDelaysMs.length) {
        return body
      }

      await this.delay(retryDelaysMs[attempt])
    }

    if (latestBody) return latestBody
    throw new Error('Falha ao carregar meteorologia do aeroporto')
  }

  private delay(milliseconds: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, milliseconds))
  }

  private mapMetarWeatherReport(
    data: WeatherReportDataWeatherMetarSummary,
  ): AirportWeatherReport<ParsedMETAR> {
    const summary = data.summary
    const rawText = summary.raw_text?.trim() || undefined
    const summaryFields = summary as Record<string, unknown>

    return {
      report_type: data.report_type as 'METAR',
      airport_icao: data.airport_icao,
      payload: rawText ? [rawText] : [],
      summary: {
        report_type: summary.report_type,
        station: summary.station,
        expired: summary.expired,
        flight_category: summary.flight_category,
        observed_at: summary.observed_at,
        raw_text: rawText,
        ceiling_feet: summary.ceiling_feet,
        cloud_layers: summary.cloud_layers ?? undefined,
        visibility: summary.visibility,
        wind: summary.wind,
        wind_direction_degrees: readSummaryNumber(
          summaryFields,
          WEATHER_METAR_SUMMARY_KEYS.windDirectionDegrees,
        ),
        wind_speed_knots: readSummaryNumber(
          summaryFields,
          WEATHER_METAR_SUMMARY_KEYS.windSpeedKnots,
        ),
        visibility_meters: readSummaryNumber(
          summaryFields,
          WEATHER_METAR_SUMMARY_KEYS.visibilityMeters,
        ),
        temperature_celsius: readSummaryNumber(
          summaryFields,
          WEATHER_METAR_SUMMARY_KEYS.temperatureCelsius,
        ),
        dewpoint_celsius: readSummaryNumber(
          summaryFields,
          WEATHER_METAR_SUMMARY_KEYS.dewpointCelsius,
        ),
        altimeter_hpa: readSummaryNumber(summaryFields, WEATHER_METAR_SUMMARY_KEYS.altimeterHpa),
      },
      cache: this.mapWeatherCache(data.cache),
    }
  }

  private mapTafWeatherReport(
    data: WeatherReportDataWeatherTafSummary,
  ): AirportWeatherReport<Record<string, unknown>> {
    const summary = data.summary
    const rawText = summary.raw_text?.trim() || undefined

    return {
      report_type: data.report_type as 'TAF',
      airport_icao: data.airport_icao,
      payload: rawText ? [rawText] : [],
      summary: {
        report_type: summary.report_type,
        station: summary.station,
        expired: summary.expired,
        flight_category: summary[WEATHER_TAF_SUMMARY_KEYS.worstFlightCategory],
        issued_at: summary.issued_at,
        valid_from: summary[WEATHER_TAF_SUMMARY_KEYS.validFrom],
        valid_to: summary[WEATHER_TAF_SUMMARY_KEYS.validTo],
        raw_text: rawText,
        periods: summary.periods,
        worst_flight_category: summary[WEATHER_TAF_SUMMARY_KEYS.worstFlightCategory],
      },
      cache: this.mapWeatherCache(data.cache),
    }
  }

  private mapWeatherCache(cache: WeatherCacheMetadata): AirportWeatherCache {
    return {
      provider: cache.provider,
      provider_version: cache.provider_version,
      provider_status: cache.provider_status,
      state: this.toWeatherCacheState(cache.state),
      cached_at: cache.cached_at,
      expires_at: cache.expires_at,
      stale: cache.stale,
    }
  }

  private toWeatherCacheState(state: string | undefined): AirportWeatherCacheState | undefined {
    if (state === 'PENDING' || state === 'READY' || state === 'NO_REPORT' || state === 'FAILED') {
      return state
    }
    return undefined
  }

  async getAirportFrequencies(ident: string, cache = true): Promise<AirportFrequenciesPayload> {
    const response = await this.get<AirportFrequenciesPayload>(
      `/airports/${encodeURIComponent(ident)}/frequencies${this.buildQuery({ cache })}`,
    )
    const isSuccess = this.isSuccessfulApiResponse(response)
    if (isSuccess && response.payload) return response.payload as AirportFrequenciesPayload
    throw new Error(response.error || 'Falha ao carregar frequências do aeroporto')
  }

  async getNearbyAirports(params: {
    lat: number
    lon: number
    distance_km?: number
    limit?: number
  }): Promise<AirportRecord[]> {
    const response = await this.get<AirportRecord[]>(`/airports/nearby${this.buildQuery(params)}`)
    const isSuccess = this.isSuccessfulApiResponse(response)
    if (isSuccess) return (response.payload as AirportRecord[] | undefined) ?? []
    throw new Error(response.error || 'Falha ao carregar aeroportos próximos')
  }

  async getAirportDistance(from: string, to: string): Promise<AirportDistanceResult> {
    const response = await this.get<AirportDistanceResult>(
      `/airports/distance${this.buildQuery({ from, to })}`,
    )
    const isSuccess = this.isSuccessfulApiResponse(response)
    if (isSuccess && response.payload) return response.payload as AirportDistanceResult
    throw new Error(response.error || 'Falha ao calcular distância entre aeroportos')
  }

  async getJobStatus(): Promise<JobStatusPayload> {
    const response = await this.get<JobStatusPayload>('/jobs/status')
    const isSuccess = this.isSuccessfulApiResponse(response)
    if (isSuccess && response.payload) return response.payload as JobStatusPayload
    throw new Error(response.error || 'Falha ao carregar status dos jobs')
  }

  async queueAnacSync(): Promise<{ status?: string }> {
    const response = await this.post<{ status?: string }>('/jobs/anac/sync')
    const isSuccess = this.isSuccessfulApiResponse(response)
    if (isSuccess) return (response.payload as { status?: string } | undefined) ?? {}
    throw new Error(response.error || 'Falha ao enfileirar sincronização ANAC')
  }

  async queueAirportImport(source_url?: string): Promise<{ status?: string }> {
    const response = await this.post<{ status?: string }>(
      '/jobs/airports/import',
      source_url ? { source_url } : undefined,
    )
    const isSuccess = this.isSuccessfulApiResponse(response)
    if (isSuccess) return (response.payload as { status?: string } | undefined) ?? {}
    throw new Error(response.error || 'Falha ao enfileirar importação de aeroportos')
  }

  async listNotificationTemplates(
    params?: NotificationTemplateListParams,
  ): Promise<NotificationTemplateRecord[]> {
    return notificationAdminService.listTemplates(params)
  }

  async listNotificationTemplateVersions(code: string): Promise<NotificationTemplateRecord[]> {
    return notificationAdminService.listTemplateVersions(code)
  }

  async upsertNotificationTemplate(
    payload: NotificationTemplateUpsertRequest,
  ): Promise<NotificationTemplateRecord> {
    return notificationAdminService.upsertTemplate(payload)
  }

  async createNotificationBroadcast(
    payload: NotificationBroadcastRequest,
  ): Promise<NotificationBroadcastResult> {
    return notificationAdminService.createBroadcast(payload)
  }

  async listAdminBroadcasts(
    params?: NotificationBroadcastListParams,
  ): Promise<NotificationBroadcastListResult> {
    return notificationAdminService.listBroadcasts(params)
  }

  async listNotificationEvents(
    params?: NotificationEventListParams,
  ): Promise<NotificationEventRecord[]> {
    return notificationAdminService.listEvents(params)
  }

  async listAdminNotifications(
    params?: NotificationAdminListParams,
  ): Promise<NotificationRecord[]> {
    return notificationAdminService.listNotifications(params)
  }

  async listNotificationDeliveries(
    params?: NotificationDeliveryListParams,
  ): Promise<NotificationDeliveryRecord[]> {
    return notificationAdminService.listDeliveries(params)
  }

  // GET /notifications/admin/acknowledgements — only limit/before/notification_id/user_id
  // are supported server-side (application.NotificationAcknowledgementListFilter).
  async listAdminAcknowledgements(
    params?: NotificationAcknowledgementListParams,
  ): Promise<NotificationAcknowledgementRecord[]> {
    return notificationAdminService.listAcknowledgements(params)
  }

  // GET /notifications/admin/escalations — only limit/before/notification_id/status are
  // supported server-side (application.NotificationEscalationListFilter).
  async listAdminEscalations(
    params?: NotificationEscalationListParams,
  ): Promise<NotificationEscalationRecord[]> {
    return notificationAdminService.listEscalations(params)
  }

  async getQualificationReport(): Promise<QualificationReport> {
    const response = await this.get<QualificationReport>('/qualifications/report')
    const isSuccess = this.isSuccessfulApiResponse(response)
    if (isSuccess && response.payload) return response.payload as QualificationReport
    throw new Error(response.error || 'Falha ao carregar relatório de qualificações')
  }

  async getAircraftOwners(id: string): Promise<AircraftOwner[]> {
    const response = await this.get<AircraftOwner[]>(`/aircrafts/${encodeURIComponent(id)}/owners`)
    const isSuccess = this.isSuccessfulApiResponse(response)
    if (isSuccess) return (response.payload as AircraftOwner[] | undefined) ?? []
    throw new Error(response.error || 'Falha ao carregar cotistas da aeronave')
  }

  async getAircraftDocuments(id: string): Promise<DocumentVaultDocument[]> {
    const response = await this.get<DocumentVaultDocument[]>(
      `/aircrafts/${encodeURIComponent(id)}/documents`,
    )
    const isSuccess = this.isSuccessfulApiResponse(response)
    if (isSuccess) return (response.payload as DocumentVaultDocument[] | undefined) ?? []
    throw new Error(response.error || 'Falha ao carregar documentos da aeronave')
  }

  async listDueDocuments(params?: {
    before?: string
    limit?: number
  }): Promise<DocumentVaultDocument[]> {
    const response = await this.get<DocumentVaultDocument[]>(
      `/document-vault/reminders/due${this.buildQuery(params)}`,
    )
    const isSuccess = this.isSuccessfulApiResponse(response)
    if (isSuccess) return (response.payload as DocumentVaultDocument[] | undefined) ?? []
    throw new Error(response.error || 'Falha ao carregar documentos pendentes')
  }

  async getAirportRunways(ident: string): Promise<AirportRunway[]> {
    const response = await this.get<{ runways: AirportRunway[] }>(
      `/airports/${encodeURIComponent(ident)}/runways`,
    )
    const isSuccess = this.isSuccessfulApiResponse(response)
    if (!isSuccess) {
      throw new Error(response.error || 'Falha ao carregar pistas do aeroporto')
    }
    const runways = (response.payload as { runways?: AirportRunway[] } | undefined)?.runways
    if (!Array.isArray(runways)) {
      throw new Error('Resposta de pistas do aeroporto inválida')
    }
    return runways
  }

  async getAirportImportHistory(limit = 10): Promise<AirportImportHistoryPayload> {
    const response = await this.get<AirportImportHistoryPayload>(
      `/airports/dataset/history${this.buildQuery({ limit })}`,
    )
    const isSuccess = this.isSuccessfulApiResponse(response)
    if (isSuccess && response.payload) return response.payload as AirportImportHistoryPayload
    throw new Error(response.error || 'Falha ao carregar histórico do dataset aeronáutico')
  }

  async createAirport(payload: {
    ident: string
    name: string
    type: string
    latitude: number
    longitude: number
    icao?: string
    iata?: string
    country?: string
    region?: string
    municipality?: string
    elevation_m?: number
    gps_code?: string
    local_code?: string
    scheduled_service?: boolean
  }): Promise<AirportRecord> {
    const response = await this.request<AirportRecord>('/airports', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
    if (response.payload) return response.payload as AirportRecord
    const isSuccess = this.isSuccessfulApiResponse(response)
    if (isSuccess) return payload as AirportRecord
    throw new ApiError(response.error || 'Falha ao criar aeroporto', response.error_code)
  }

  async updateAirport(
    id: string,
    payload: Partial<{
      ident: string
      name: string
      type: string
      latitude: number
      longitude: number
      icao: string
      iata: string
      country: string
      region: string
      municipality: string
      elevation_m: number
      gps_code: string
      local_code: string
      scheduled_service: boolean
    }>,
  ): Promise<AirportRecord> {
    const response = await this.request<AirportRecord>(`/airports/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    })
    const isSuccess = this.isSuccessfulApiResponse(response)
    if (isSuccess && response.payload) return response.payload as AirportRecord
    throw new ApiError(response.error || 'Falha ao atualizar aeroporto', response.error_code)
  }

  async searchNavaids(params?: {
    q?: string
    type?: string
    limit?: number
    offset?: number
  }): Promise<NavaidRecord[]> {
    const response = await this.get<NavaidRecord[]>(`/navaids${this.buildQuery(params)}`)
    const isSuccess = this.isSuccessfulApiResponse(response)
    if (isSuccess) return (response.payload as NavaidRecord[] | undefined) ?? []
    throw new Error(response.error || 'Falha ao carregar navaids')
  }

  async getNavaid(ident: string): Promise<NavaidRecord> {
    const response = await this.get<NavaidRecord>(`/navaids/${encodeURIComponent(ident)}`)
    const isSuccess = this.isSuccessfulApiResponse(response)
    if (isSuccess && response.payload) return response.payload as NavaidRecord
    throw new Error(response.error || 'Falha ao carregar navaid')
  }

  async searchWaypoints(params?: {
    q?: string
    type?: string
    limit?: number
    offset?: number
  }): Promise<WaypointRecord[]> {
    const response = await this.get<WaypointRecord[]>(`/waypoints${this.buildQuery(params)}`)
    const isSuccess = this.isSuccessfulApiResponse(response)
    if (isSuccess) return (response.payload as WaypointRecord[] | undefined) ?? []
    throw new Error(response.error || 'Falha ao carregar waypoints')
  }
  async getDatasetVersion(): Promise<DatasetVersion | null> {
    const response = await this.get<DatasetVersion>('/airports/dataset/version')
    const isSuccess = this.isSuccessfulApiResponse(response)
    if (isSuccess && response.payload) return response.payload as DatasetVersion
    return null
  }

  async getAircraft(id: string): Promise<AircraftRecord> {
    const response = await this.get<AircraftRecord>(`/aircrafts/${encodeURIComponent(id)}`)
    const isSuccess = this.isSuccessfulApiResponse(response)
    if (isSuccess && response.payload) return response.payload as AircraftRecord
    throw new Error(response.error || 'Falha ao carregar aeronave')
  }

  async createAircraft(payload: {
    tail_number: string
    active?: boolean
    operational_range_km?: number
    anac_syncable?: boolean
  }): Promise<AircraftRecord> {
    const response = await this.post<AircraftRecord>('/aircrafts', payload)
    const isSuccess = this.isSuccessfulApiResponse(response)
    if (isSuccess && response.payload) return response.payload as AircraftRecord
    throw new ApiError(response.error || 'Falha ao criar aeronave', response.error_code)
  }

  async updateAircraft(
    id: string,
    payload: {
      tail_number?: string
      active?: boolean
      operational_range_km?: number
      anac_syncable?: boolean
      weight_specification?: {
        empty_weight_kg?: number
        max_takeoff_weight_kg?: number
        max_landing_weight_kg?: number
        useful_load_kg?: number
      } | null
    },
  ): Promise<AircraftRecord> {
    const response = await this.request<AircraftRecord>(`/aircrafts/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    })
    const isSuccess = this.isSuccessfulApiResponse(response)
    if (isSuccess && response.payload) return response.payload as AircraftRecord
    throw new ApiError(response.error || 'Falha ao atualizar aeronave', response.error_code)
  }
}

// Export singleton instance
export const apiService = new ApiService()
export default apiService
